import { Button, Text, TextField } from '@react-spectrum/s2';
import ChevronRight from '@react-spectrum/s2/icons/ChevronRight';
import { useCallback, useState } from 'react';
import { Form } from 'react-aria-components';

import { TypewriterOnce } from '../components/TypewriterOnce';

type PhoneScreenProps = {
	// Re-seeds the field when the user navigates back here: the wizard
	// remounts screens on every transition, so local state doesn't survive.
	initialPhone?: string;
	onSubmit: (phone: string) => void;
};

const labelStyle: React.CSSProperties = {
	textTransform: 'uppercase',
	letterSpacing: '0.12em',
	fontSize: 12,
	fontWeight: 600,
	color: 'var(--text-muted)',
};

export const PhoneScreen = ({ initialPhone, onSubmit }: PhoneScreenProps) => {
	const [phone, setPhone] = useState(initialPhone ?? '');
	const [typingDone, setTypingDone] = useState(false);
	const handleTypingDone = useCallback(() => setTypingDone(true), []);

	const isValid = phone.replace(/\D/g, '').length >= 10;

	const handleSubmit = useCallback(() => {
		if (!isValid) return;
		onSubmit(phone);
	}, [isValid, phone, onSubmit]);

	return (
		<div className="flex h-full w-full items-center justify-center">
			<div className="w-full max-w-[760px] px-8">
				<div
					className="mb-8 overflow-hidden"
					style={{
						width: 56,
						height: 56,
						borderRadius: 14,
						background: 'white',
					}}
				>
					<img
						src="/phone-icon.svg"
						alt=""
						style={{ width: '100%', height: '100%', display: 'block' }}
					/>
				</div>

				<h2
					className="mb-4"
					style={{
						fontSize: 'clamp(26px, 4vw, 40px)',
						fontWeight: 700,
						letterSpacing: '-0.02em',
						lineHeight: 1.15,
						color: 'var(--text-primary)',
					}}
				>
					<TypewriterOnce
						text="One more setup thing."
						onDone={handleTypingDone}
					/>
				</h2>
				<p
					className="mb-10"
					style={{
						fontSize: 18,
						lineHeight: 1.5,
						color: 'var(--text-primary)',
						opacity: typingDone ? 1 : 0,
						transition: 'opacity 400ms ease',
					}}
				>
					I&apos;ll be working while you&apos;re out living your life. Drop your
					number — text me anytime, I&apos;ll text back.
				</p>

				<div
					style={{
						opacity: typingDone ? 1 : 0,
						transition: 'opacity 400ms ease',
						pointerEvents: typingDone ? 'auto' : 'none',
					}}
				>
					<Form
						className="flex flex-col gap-6"
						onSubmit={(e) => {
							e.preventDefault();
							handleSubmit();
						}}
					>
						<TextField
							label={<span style={labelStyle}>Phone number</span>}
							placeholder="(555) 123-4567"
							value={phone}
							onChange={setPhone}
							type="tel"
							size="L"
						/>

						<p
							style={{
								fontSize: 13,
								lineHeight: 1.5,
								color: 'var(--text-muted)',
							}}
						>
							I consent to receive phone calls and text messages from Bridgeway
							at the number provided. Standard messaging and call rates may
							apply. Opt out at any time by replying STOP.
						</p>

						<div className="mt-4">
							<Button
								variant="primary"
								staticColor="black"
								fillStyle="fill"
								size="L"
								type="submit"
								isDisabled={!isValid}
							>
								<Text>Send me a text</Text>
								<ChevronRight />
							</Button>
						</div>
					</Form>
				</div>
			</div>
		</div>
	);
};
